import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button, Popover, Tooltip, Typography, Empty } from 'antd';
import { ArrowLeftOutlined, HistoryOutlined } from '@ant-design/icons';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { loadPref, readLocalPref, savePref } from '../utils/prefs';

// Navigation helpers, drawn by Layout:
//
// "Back to ..." (BackChip, above every page). Layout keeps a trail of the
// Harbor pages visited in this browser tab (sessionStorage, so a refresh
// keeps it; a new tab starts fresh), each with a readable label. The chip
// goes to the previous page's full URL, filters and all, and says where.
// Opened directly (a link, an alert, a new tab), a page with a natural list
// falls back to it (useBackFallback); otherwise no chip.
//
// Recent (RecentMenu, the clock in the top bar): the last pages visited, kept
// per user (UserPreferences 'nav.recent', so it follows them to any
// computer). Moving between tabs or filters on one page updates its entry
// rather than adding one; a different subject (another agent on Agent
// Summary) is its own entry.
//
// Pages sharpen their own label with usePageLabel ("Agent Summary: Tammy
// Hanson · Errors"); the part before " · " names the subject.

const KEY = 'harborTrail';
const MAX = 30;
const RECENT_KEY = 'nav.recent';
const RECENT_MAX = 15;
const read = () => {
  try { return JSON.parse(sessionStorage.getItem(KEY) || '[]'); } catch { return []; }
};
const write = (t) => {
  try { sessionStorage.setItem(KEY, JSON.stringify(t.slice(-MAX))); } catch { /* storage off: no trail */ }
};
const pathOf = (url) => url.split('?')[0];
const subjectOf = (label) => (label || '').split(' · ')[0];

const NavCtx = createContext({ setLabel: () => {}, setFallback: () => {}, back: null, recent: [] });

// The current page's label, shown on the next page's chip and in Recent.
export function usePageLabel(label) {
  const { setLabel } = useContext(NavCtx);
  useEffect(() => { if (label) setLabel(label); }, [label, setLabel]);
}

// Where the chip goes when there is no earlier Harbor page (opened directly).
// No path: leaves it alone (a page shown in another page's panel).
export function useBackFallback(path, label) {
  const { setFallback } = useContext(NavCtx);
  useEffect(() => {
    if (!path) return undefined;
    setFallback({ path, label });
    return () => setFallback(null);
  }, [path, label, setFallback]);
}

// Layout wraps everything (header and page) in this; defaultLabel(pathname)
// names pages that set no label of their own (the menu's name for them).
export function NavTrailProvider({ defaultLabel, userId, children }) {
  const location = useLocation();
  const navType = useNavigationType();
  const navigate = useNavigate();
  const url = location.pathname + location.search;
  const [trail, setTrail] = useState(read);
  const [fallback, setFallback] = useState(null);
  const labelFor = useRef(defaultLabel);
  labelFor.current = defaultLabel;

  // Record each move. The chip's own moves carry the index to return to; the
  // browser's Back is treated the same when it lands on the previous entry.
  // A layout effect, so the new page is on the trail before its own
  // usePageLabel (a plain effect) names it.
  useLayoutEffect(() => {
    setTrail(() => {
      let t = read();
      const entry = { url, label: labelFor.current(location.pathname) };
      const backTo = location.state?.trailIndex;
      if (Number.isInteger(backTo) && t[backTo]?.url === url) {
        t = t.slice(0, backTo + 1);
      } else if (t.length && t[t.length - 1].url === url) {
        // same page (a refresh): keep it
      } else if (navType === 'POP' && t.length >= 2 && t[t.length - 2].url === url) {
        t = t.slice(0, -1);
      } else if (navType === 'REPLACE' && t.length) {
        // A page updating its own URL (filters, another agent), or a redirect
        // (/ to My Summary, a page you may not open): same entry, new URL;
        // the page names it again. A redirect is not a page to go back to.
        t = [...t.slice(0, -1), entry];
      } else {
        t = [...t, entry];
      }
      write(t);
      return t;
    });
  }, [url]); // eslint-disable-line react-hooks/exhaustive-deps

  const setLabel = useCallback((label) => {
    setTrail((cur) => {
      if (!cur.length || cur[cur.length - 1].label === label) return cur;
      const t = [...cur.slice(0, -1), { ...cur[cur.length - 1], label }];
      write(t);
      return t;
    });
  }, []);

  // -- Recent ---------------------------------------------------------------
  const [recent, setRecent] = useState(() => (userId && readLocalPref(userId, RECENT_KEY)) || []);
  const loaded = useRef(false);
  useEffect(() => {
    if (!userId) return;
    // Saved only once the server's list is in, so a fresh browser does not
    // overwrite it with the one page it has seen.
    loadPref(userId, RECENT_KEY).then((v) => {
      setRecent((cur) => {
        const m = merge(Array.isArray(v) ? v : [], cur);
        savePref(userId, RECENT_KEY, m, 1500);
        return m;
      });
      loaded.current = true;
    });
  }, [userId]);
  const top = trail[trail.length - 1];
  useEffect(() => {
    // '/' only redirects to My Summary: not a page of its own.
    if (!userId || !top || top.url === '/') return;
    setRecent((cur) => {
      const item = { url: top.url, label: top.label, at: Date.now() };
      const key = (x) => `${pathOf(x.url)}|${subjectOf(x.label)}`;
      let next;
      if (cur[0] && (cur[0].url === item.url || key(cur[0]) === key(item))) {
        next = [item, ...cur.slice(1)];          // the same page: label or filters changed
      } else {
        next = [item, ...cur];
      }
      next = next.filter((x, i) => i === 0 || (x.url !== item.url && key(x) !== key(item))).slice(0, RECENT_MAX);
      if (loaded.current) savePref(userId, RECENT_KEY, next, 1500);
      return next;
    });
  }, [userId, top?.url, top?.label]); // eslint-disable-line react-hooks/exhaustive-deps

  const prev = trail.length >= 2 ? trail[trail.length - 2] : null;
  const back = prev
    ? { text: `Back to ${prev.label}`, go: () => navigate(prev.url, { state: { trailIndex: trail.length - 2 } }) }
    : fallback && fallback.path !== location.pathname
      ? { text: fallback.label, go: () => navigate(fallback.path) }
      : null;

  const ctx = { setLabel, setFallback, back, recent, currentUrl: url, navigate };
  return <NavCtx.Provider value={ctx}>{children}</NavCtx.Provider>;
}

// The server's list first, then anything newer seen here meanwhile.
function merge(saved, local) {
  const seen = new Set();
  return [...local, ...saved]
    .filter((x) => !/#undefined\b/.test(x.label || '') && x.url !== '/')   // early bad labels; '/' redirects
    .sort((a, b) => (b.at || 0) - (a.at || 0))
    .filter((x) => (seen.has(x.url) ? false : (seen.add(x.url), true)))
    .slice(0, RECENT_MAX);
}

// "<- Back to ...", above the page.
export function BackChip() {
  const { back } = useContext(NavCtx);
  if (!back) return null;
  return (
    <div className="no-print" style={{ marginBottom: 12 }}>
      <Button size="small" icon={<ArrowLeftOutlined />} onClick={back.go}>{back.text}</Button>
    </div>
  );
}

// The clock in the top bar: recent pages, newest first (not the one open).
export function RecentMenu() {
  const { recent, currentUrl, navigate } = useContext(NavCtx);
  const [open, setOpen] = useState(false);
  const items = recent.filter((r) => r.url !== currentUrl);
  const content = (
    <div style={{ width: 340, maxHeight: 420, overflowY: 'auto' }}>
      {!items.length ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No recent pages yet" /> : items.map((r) => (
        <div key={r.url} role="button" tabIndex={0}
          onClick={() => { setOpen(false); navigate(r.url); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { setOpen(false); navigate(r.url); } }}
          style={{ padding: '6px 8px', cursor: 'pointer', borderRadius: 4 }}
          className="recent-page-item">
          <Typography.Text style={{ fontSize: 13 }}>{r.label}</Typography.Text>
        </div>
      ))}
    </div>
  );
  return (
    <Popover content={content} title="Recent pages" trigger="click" open={open} onOpenChange={setOpen}
      placement="bottomRight" arrow={false}>
      <Tooltip title="Recent pages">
        <Button type="text" aria-label="Recent pages"
          icon={<HistoryOutlined style={{ color: '#fff', fontSize: 18 }} />}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
      </Tooltip>
    </Popover>
  );
}
